import { create } from 'zustand'

import type { SearchType } from '@/components/pages/search/SearchSelector/SearchSelector.types'
import useAiSearchStore from '@/stores/aiSearchStore'

type SearchSelectorState = {
	searchType: SearchType
	lyricsRestrictionCommitted: boolean
	inputDraft: string
	setSearchType: (searchType: SearchType) => void
	setLyricsRestrictionCommitted: (value: boolean) => void
	setInputDraft: (value: string) => void
	clearInputDraft: () => void
}

const useSearchSelectorStore = create<SearchSelectorState>((set) => ({
	searchType: 'keywords',
	lyricsRestrictionCommitted: false,
	inputDraft: '',
	/* lyricsRestrictionCommitted handles restricting Algolia Configure props to only the Lyrics attribute
	 * and is owned by the onSubmit autocomplete handler so that
	 * switching the selector never re-fires a search before the user has submitted.
	 */
	setSearchType: (searchType) => set({ searchType }),
	setLyricsRestrictionCommitted: (value) =>
		set({ lyricsRestrictionCommitted: value }),
	setInputDraft: (value) => set({ inputDraft: value }),
	clearInputDraft: () => set({ inputDraft: '' }),
}))

type ParsedSearchQuery = Record<string, unknown>

export function applySearchTypeAfterUrlParse(args: {
	user: User | null
	parsed: ParsedSearchQuery
	clearedAiStateFromUrl: boolean
}): void {
	const { user, parsed, clearedAiStateFromUrl } = args

	const searchByAudioRaw = parsed.searchByAudio
	const searchByPromptRaw = parsed.searchByPrompt

	const hasAudioParam =
		typeof searchByAudioRaw === 'string' && searchByAudioRaw.length > 0

	const hasPromptParam =
		!!user &&
		typeof searchByPromptRaw === 'string' &&
		searchByPromptRaw.length > 0

	const aiAfterParse = useAiSearchStore.getState()
	const setSearchType = useSearchSelectorStore.getState().setSearchType

	if (
		hasAudioParam ||
		(!clearedAiStateFromUrl && aiAfterParse.searchByAudio?.url)
	) {
		setSearchType('reference')
	} else if (
		hasPromptParam ||
		(!clearedAiStateFromUrl && user && aiAfterParse.searchByPrompt)
	) {
		setSearchType('prompt')
	} else if (clearedAiStateFromUrl) {
		setSearchType('keywords')
	}
}

export function syncSearchTypeFromAiStore(user: User | null): void {
	const { searchByAudio, searchByPrompt } = useAiSearchStore.getState()
	const setSearchType = useSearchSelectorStore.getState().setSearchType

	if (searchByAudio?.url) {
		setSearchType('reference')
		return
	}

	if (user && searchByPrompt) {
		setSearchType('prompt')
	}
}

export function isKeywordsSearchMode(searchType: SearchType): boolean {
	return searchType === 'keywords'
}

export function isPromptSearchMode(searchType: SearchType): boolean {
	return searchType === 'prompt'
}

export function isReferenceSearchMode(searchType: SearchType): boolean {
	return searchType === 'reference'
}

export function isLyricsSearchMode(searchType: SearchType): boolean {
	return searchType === 'lyrics'
}

export function shouldOfferPromptAiInTypeahead(
	searchType: SearchType
): boolean {
	return isPromptSearchMode(searchType)
}

export default useSearchSelectorStore
